/**
 * DD-3687: the read-only archive of orders imported from a customer's previous
 * system. Clinic, lab and senders are the names the old system recorded, not
 * links, so none of these fields resolves to a Denthub entity.
 *
 * Dates arrive as epoch milliseconds, matching the rest of the schema.
 */

/** The header fields a user may search on. Values match the GraphQL enum. */
export type HistoricalOrderTotals = {
    exclVat?: number;
    vat?: number;
    inclVat?: number;
    currency?: string;
};

export type HistoricalOrderLine = {
    /** 'action' or 'material'. */
    type: string;
    code?: string;
    description?: string;
    quantity?: number;
    unit?: string;
    unitPrice?: number;
    /** A fraction, not a percentage. */
    vatRate?: number;
    vatAmount?: number;
    lineTotal?: number;
};

export type HistoricalOrderMessage = {
    sentAt?: number;
    senderName?: string;
    /** 'clinic', 'lab' or 'other'. */
    senderSide?: string;
    text?: string;
};

export type HistoricalOrderAttachment = {
    _id: string;
    fileName?: string;
    /** Bytes. */
    size?: number;
};

export type HistoricalOrderExtraDetail = {
    label: string;
    value?: string;
};

export type HistoricalOrderListItem = {
    _id: string;
    orderNumber?: string;
    orderDate?: number;
    customerName?: string;
    labName?: string;
    patientReference?: string;
    totals?: HistoricalOrderTotals;
};

export type HistoricalOrder = {
    _id: string;
    orderNumber?: string;
    orderDate?: number;
    deliveryDate?: number;
    customerName?: string;
    deliveryDestinationName?: string;
    labName?: string;
    patientReference?: string;
    status?: string;
    totals?: HistoricalOrderTotals;
    lines: HistoricalOrderLine[];
    messages: HistoricalOrderMessage[];
    attachments: HistoricalOrderAttachment[];
    comments?: string;
    extraDetails: HistoricalOrderExtraDetail[];
    sourceSystem?: string;
    importBatchId?: string;
    importedAt?: number;
};

export type HistoricalOrderSearchResult = {
    rows: HistoricalOrderListItem[];
    hasMore: boolean;
};

/** Matches the backend's floor; a shorter value scans the whole archive. */
export const MINIMUM_HISTORICAL_SEARCH_LENGTH = 3;

/** Page size for the result list. */
export const HISTORICAL_ORDERS_PAGE_SIZE = 25;
